import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { get } from 'lodash'
import { AppContext } from '../Context'
import { getContactReference, sendContactMessage } from '../services'
import StyledSelector from '../../common/StyledSelector'
import Input from '../../common/Input'
import TextArea from '../../common/TextArea'
import PageLayout from '../components/PageLayout'
import { checkConfirmEmail, checkEmail } from '../../common/validator'
import theme from '../../common/theme'
import { getBabysittingModeFrom } from '../../common/BabysittingModes'
import { getRecords } from '../services/record-services'
import { Error, Success } from '../../common/Text'
import { htmlEncode } from '../../common/util'

const ContactPageLayout = styled(PageLayout)`
  .content {
    margin: 0 20px;
  }
  .field {
    align-items: stretch;
    margin-bottom: 10px;
  }
  input, select {
    background-color: ${ theme.palette.light_grey };
    border: 1px solid ${ theme.palette.grey };
  }

`

function ContactPage({ ...props }) {
  const {
    babysittingModes,
    employee,
    setEmployee,
    updateEmployeeContact
  } = useContext(AppContext)

  const [contactForm, setContactForm] = useState({})
  const [contactRef, setContactRef] = useState(null)

  const [success, setSuccess] = useState(null)
  const [error, setError] = useState({})
  const [canSubmit, setCanSubmit] = useState(false)
  const [files, setFiles] = useState(null)

  useEffect(() => {
    const getContactRef = async () => {
      const result = await getContactReference()
      setContactRef(result)
    }
    if (!contactRef) {
      getContactRef()
    }
  }, [contactRef, setContactRef])

  useEffect(()=>{
      initForm()
  },[employee])

  const initForm = () => {
    const email = get(employee, 'email')
    const emailFields = (email? {
      email: email,
      confirmEmail: email,
    }: {})

    const newForm = {
      reason: '',
      file: '',
      message: '',
      ...emailFields
    }

    setContactForm(newForm)
  }

  useEffect(() => {
    const newError = {}
    newError.email = checkEmail(contactForm.email)
    newError.confirmEmail = checkConfirmEmail(contactForm.email, contactForm.confirmEmail)
    setError(newError)
    setCanSubmit(get(contactForm, 'reason') && get(contactForm, 'message')
      && get(contactForm, 'email') && get(contactForm, 'confirmEmail') && !newError.email && !newError.confirmEmail)
  }, [contactForm])


  useEffect(() => {
    const fetchRecords = async () => {
      // We retrieve every file related to the employee :
      const result = await getRecords(fields)
      const records = result.data.getRecords
      // We need to exclude the files that are being created at this moment :
      const recordsSubmittedOnce = records.filter(record => record.status !== 'CREATED')
      // We add new fields (for each record) that we'll use in the combo as keys and values :
      recordsSubmittedOnce.forEach(record => {
        record.code = record._id
        const babysittingModeName = getBabysittingModeFrom(babysittingModes, record.babysittingMode, 'name')
        record.name = `[${ record.reference }] - [${ babysittingModeName }] - [${ record.child.firstName }]`
      })
      // We also need to allow the employee to not select any file cause it's not mandatory :
      recordsSubmittedOnce.unshift({_id: undefined, code: '', name: ''})
      setFiles(recordsSubmittedOnce)
    }
    if (babysittingModes && !files) {
      fetchRecords()
    }
  }, [files, babysittingModes])

  const onFormValueChange = (key) => {
    return (value) => {
      const newForm = {
        ...contactForm,
        [key]: value
      }
      setSuccess('')
      setContactForm(newForm)
    }
  }

  const handlerPaste = (e) => {
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()
  }

  const sendMessage = async() => {
    if(!get(employee, 'email')){
      employee.email = get(contactForm, 'email')
      const res = await updateEmployeeContact(employee)
      res && res.data && setEmployee(res.data.updateEmployee)
    }

    // Get record info if present
    let record = {}
    if (contactForm.file) {
      const file = files.find(f => f._id === contactForm.file)
      record = {
        recordId: file._id,
        recordReference: file.reference
      }
    }

    const contactMessage = {
      object: contactForm.reason,
      ...record,
      email: employee.email,
      message: htmlEncode(contactForm.message)
    }

    const response = await sendContactMessage(contactMessage)
    if (response && response.errors){
      const newError = {message: 'Impossible d\'envoyer le message de contact.'}
      setError(newError)
      setSuccess("")
    } else {
      setError({})
      setSuccess("Message envoyé avec succès.")
      initForm()
    }
    return false
  }

  return (
    <ContactPageLayout
      title="Contactez Pimprenelle"
      prevLink="/faq"
      actions={ {
        submitTxt: 'Envoyer',
        onSubmit: sendMessage,
        cancelLink: '/faq',
        disableSubmit: !canSubmit
      } }
    >
      <StyledSelector
        direction="column"
        options={ get(contactRef, 'objects', []) }
        label="Objet"
        value={ contactForm.reason ? contactForm.reason : '' }
        placeholder="Sélectionner le dossier concerné"
        onValueChange={ onFormValueChange('reason') }
      />

      { files && files.length > 1 &&
        <StyledSelector
          direction="column"
          options={ files }
          label="Dossier"
          value={ contactForm.file ? contactForm.file : '' }
          placeholder="Choisissez un dossier"
          onValueChange={ onFormValueChange('file') }
        />
      }

      { employee && !get(employee, 'email') &&
      <React.Fragment>
        <Input direction="column"
               label="Adresse email"
               placeholder="Adresse email"
               value={ get(contactForm, 'email') }
               error={ error.email }
               validated={ get(contactForm, 'email') && !error.email }
               onValueChange={ onFormValueChange('email') }
        />
        <Input direction="column"
               label="Confirmer votre adresse email"
               placeholder="Adresse email"
               value={ get(contactForm, 'confirmEmail') }
               error={ error.confirmEmail }
               validated={ get(contactForm, 'confirmEmail') && !error.confirmEmail }
               onValueChange={ onFormValueChange('confirmEmail') }
               onPaste={ handlerPaste }
        />
      </React.Fragment> }

      <TextArea direction="column"
        label="Votre message"
        rows={ 15 }
        value={ get(contactForm, 'message') }
        onValueChange={ onFormValueChange('message') }
      />
      {error.message && <Error>{error.message}</Error>}
      {success && <Success>{success}</Success>}


    </ContactPageLayout>
  )
}

export default ContactPage

const fields = `
  _id
  ratpId
  reference
  status
  babysittingMode
  child {
    firstName
  }
`
